import { unpackRules } from '@casl/ability/extra';

import { AppAbility } from '@acl/config';
import { UserSessionData } from '@api/gql/graphql';
import { client } from '@hooks/useApolloClient';
import { setLocalStorageItem } from '@src/utils/helperFunctions';
// eslint-disable-next-line import/no-cycle
import { handleMenuCollapsed } from '@store/actions/layout';
import { getInitialAlerts } from '@store/actions/tasks';
import { cache } from '@store/apollo/cache';
import { purgeCache } from '@store/apollo/persist';
import {
  hasChangedPermissionVar,
  isLoggedInVar,
  layoutSkinStateVar,
  userAlertsVar,
  userSessionVar
} from '@store/reactiveVars';

// Staff-context fields the backend resolves from the auth token. A silent
// token-renew can transiently return these as null while the rest of the
// session is intact — persisting that half-null session wipes the working
// session and breaks every location-scoped action (patient registration, order
// creation, scoped queries). Keep the last-known values when a refresh nulls
// them for the SAME user, so a transient backend hiccup doesn't clobber it.
export const mergeSessionContext = (
  existing: UserSessionData | null | undefined,
  incoming: UserSessionData
): UserSessionData => {
  if (!existing || existing.userId !== incoming.userId) return incoming;
  return {
    ...incoming,
    staffRelationId: incoming.staffRelationId ?? existing.staffRelationId,
    organizationId: incoming.organizationId ?? existing.organizationId,
    locationId: incoming.locationId ?? existing.locationId,
    locationName: incoming.locationName ?? existing.locationName
  };
};

export const handleLogin = (
  dataPassed: {
    session: UserSessionData;
    permissions: string;
  },
  ability: AppAbility
) => {
  handleMenuCollapsed(false);
  const session = mergeSessionContext(userSessionVar(), dataPassed.session);
  userSessionVar(session);
  // setLocalStorageItem stringifies internally; passing JSON.stringify before
  // would have double-encoded the payload (the stored value would be a JSON
  // string of a JSON string). Hydration on next boot would then return a
  // string instead of the session object.
  setLocalStorageItem('userData', session);

  try {
    const parsedRules = JSON.parse(dataPassed.permissions);
    ability.update(unpackRules(parsedRules));
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[handleLogin] permissions parse failed:', err);
    }
    ability.update([]);
  }
  isLoggedInVar(true);
};

export const handleLogout = async (ability: AppAbility) => {
  const permissionState = hasChangedPermissionVar();

  // 1. Flip auth flags FIRST. Anything that subscribes to isLoggedInVar should stop
  //    issuing requests immediately, before we tear down the store underneath them.
  isLoggedInVar(false);
  userSessionVar(undefined);
  ability.update([{ subject: ['Public'], action: ['Public'] }]);

  // 2. Reset local UI state.
  userAlertsVar(getInitialAlerts());
  layoutSkinStateVar('light');

  // 3. Wipe storage. We never reach back into either after this point.
  try {
    sessionStorage.clear();
  } catch { /* private mode */
  }
  try {
    localStorage.clear();
  } catch { /* private mode */
  }

  // 4. Drain Apollo: clearStore() drops everything (cache + watched queries) without
  //    refiring them. The previous flow called clearStore() AND cache.reset() AND
  //    resetStore() in sequence — resetStore re-fires every active query, which
  //    after a logout is exactly what we don't want.
  try {
    await client.clearStore();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[handleLogout] clearStore failed:', err);
    }
  }
  cache.gc({ resetResultCache: true, resetResultIdentities: true });

  // 5. Wipe the persisted on-disk cache snapshot. Without this, the next user on
  //    this browser would briefly see the previous user's data flash on screen
  //    before the network revalidates and Apollo overwrites it.
  await purgeCache();

  hasChangedPermissionVar(!permissionState);
};
